/*
 * Copyright (c) 2025-2026.  atagon GmbH.  All rights reserved.
 *
 * This file is part of the atagon codebase and may not be copied,
 *  distributed, or modified without explicit written permission from
 * atagon GmbH. https://www.atagon.com
 */

import React, {Component} from 'react';
import DisplayIcon from "../components/Icons/DisplayIcon.jsx";
import {Containers} from "../Objects/Containers.jsx";
import DeploymentHistory from "../components/Container/DeploymentHistory.jsx";
import ContainerLogs from "../components/Container/ContainerLogs.jsx";
import NodePage from "./NodePage.jsx";
import Tabs from "../components/Tabs/Tabs.jsx";
import Tab from "../components/Tabs/Tab.jsx";
import {motion} from "motion/react";
import ContainerSettings from "../components/Container/ContainerSettings.jsx";
import BuildLogs from "../components/BuildLogs.jsx";
import {Socket} from "../Socket.js";
import {Subscription} from "../Subscription.js";
import ContainerCharts from "../components/Container/ContainerCharts.jsx";
import Strings from 'toolbox/strings';
import EmptyStateBig from "../components/EmptyState/EmptyStateBig.jsx";
import ElementDomains from "../components/ElementDomains.jsx";
import ContainerActionsButton from "../components/Container/ContainerActionsButton.jsx";
import ElementStatus from "../components/ElementStatus.jsx";
import EmptyState from "../components/EmptyState/EmptyState.jsx";

class ContainerPage extends Component {
    constructor() {
        super();
        this.state = {
            containerInfo: {}, currentTab: 0, isLoaded: false, selectedBuild: ""

        }

        this.sub = new Subscription()
    }

    async fetchData() {
        let {containerId} = this.props
        let container = await Containers(containerId).getInfo()
        this.setState({containerInfo: container ?? {}, isLoaded: true})

        this.sub.add(() => Database.table("Container", "app").onChange((change) => {
            let containerInfo = this.state.containerInfo
            containerInfo = Socket.applyChange([containerInfo], change)[0]

            this.setState({containerInfo: containerInfo})
        }, [{
            column: "containerId", value: containerId
        }]), "liveContainerData")

    }

    componentDidMount() {
        this.fetchData()
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps.containerId !== this.props.containerId) {
            this.fetchData()
        }
    }


    componentWillUnmount() {
        this.sub.unmount()
    }


    render() {
        let {containerId} = this.props
        let {containerInfo, selectedBuild, isLoaded} = this.state

        if (!isLoaded) {
            return (<div>Loading container info. {containerId}</div>)
        }

        if (Strings.isEmpty(containerInfo.indexKey)) {
            return (<div>
                <EmptyStateBig iconName={"Container"} text={`Container not found. (containerId: ${containerId}`}/>
            </div>)

        }

        return (<div>
            <div className={"overflow-hidden rounded-lg relative mb-4 mt-2 border-1 "}>

                <motion.img className="h-32 w-full object-cover lg:h-60 shadow-inner "
                            whileHover={{scale: 1.1, rotate: 1}}
                            transition={{
                                type: "spring", stiffness: 450, damping: 15
                            }}
                            src={`/images/containerImages/${containerInfo?.backgroundURL}.jpg`}
                            alt=""/>

            </div>
            <div className=" w-full bg-white rounded-lg shadow-sm mb-5">
                <div className="mx-auto flex items-center h-16 justify-between gap-x-8 lg:mx-0 px-4 py-3 ">
                    <div className="flex items-center gap-x-4">
                        <div
                            className={"h-10 w-10 flex-none rounded-lg flex justify-center items-center text-stone-600"}>
                            <DisplayIcon iconName={"Container"} className={"h-7 w-7"}/>


                        </div>

                        <h1>
                            <div className={"flex items-center gap-4"}>

                                <div
                                    className=" text-xl font-semibold leading-4 text-gray-900">{containerInfo.containerName}</div>
                                {containerInfo.targetState !== "running" ? <>
                                    <>
                                        <div
                                            className="inline-flex select-none items-center gap-1.5 rounded-md bg-blue-100 px-2.5 py-1 text-sm font-medium text-blue-700">
                                            <DisplayIcon iconName={"PiPause"}/>
                                            <span>Paused</span>
                                        </div>
                                    </>
                                </> : (<>
                                    <ElementStatus name={"Container"} elementId={containerId}/>

                                </>)}

                            </div>
                            {/*<div className="text-xs leading-6 text-gray-500">{containerInfo.nodeIndexKey}</div>*/}

                        </h1>
                    </div>
                    <div>
                        <ContainerActionsButton containerId={containerId}/>


                    </div>
                </div>
                <div>
                    <Tabs activeKey={this.state.currentTab}
                          onSelect={(number) => this.setState({currentTab: number})} variant={'default'} isBox>
                        <Tab eventKey={0}
                             iconName={"PiLog"}
                             title={"Logs"}>
                            <div className={" flex flex-row gap-1"}>

                                <div className={"w-2/3"}>
                                    {selectedBuild === "" ? (<>
                                        {containerInfo.targetState !== "running" ? <div className={"p-4 h-full"}>
                                            <EmptyState
                                                text={"The container is paused. Start it to view the live logs. "}/>
                                        </div> : (<>   <ContainerLogs nodeId={containerInfo.nodeIndexKey}
                                                                      containerId={containerInfo.indexKey}/></>)}

                                    </>) : (<>
                                        <BuildLogs buildId={selectedBuild}/>

                                    </>)}


                                </div>
                                <div className={"w-1/3"}>
                                    <DeploymentHistory selectedBuild={selectedBuild}
                                                       imageId={containerInfo.imageIndexKey}
                                                       onSelect={(newSelectedBuild) => {
                                                           this.setState({selectedBuild: newSelectedBuild})
                                                       }}
                                    />

                                </div>
                            </div>
                        </Tab>
                        <Tab eventKey={1}
                             iconName={"PiChartLine"}
                             title={"Statistics"}>
                            <ContainerCharts nodeId={containerInfo.nodeIndexKey} containerId={containerInfo.indexKey}/>

                        </Tab>
                        <Tab eventKey={2}
                             iconName={"PiGearSix"}
                             title={"Settings"}>
                            <ContainerSettings containerId={containerInfo.indexKey}/>
                        </Tab>
                        <Tab eventKey={3}
                             iconName={"Domain"}
                             title={"Domains"}>
                            <div className={"m-3 "}>
                                <ElementDomains elementId={containerInfo.indexKey} elementType={"container"}/>
                            </div>
                        </Tab>
                        <Tab eventKey={4}
                             iconName={"PiFolderSimple"}
                             title={"Files"}>
                            Files inside the container
                        </Tab>
                        <Tab eventKey={5}
                             title={"Node"}>
                            <NodePage nodeId={containerInfo.nodeIndexKey}/>

                        </Tab>
                    </Tabs>


                </div>
            </div>


        </div>);
    }
}

ContainerPage.propTypes = {};

ContainerPage.defaultProps = {};


export default ContainerPage;